/**
 * Kwolly Slideshow — Custom Element
 * Vanilla JS, no dependencies, Web Component pattern (Dawn-compatible)
 *
 * Features:
 *   - CSS-driven crossfade slide transitions
 *   - Configurable autoplay (data-autoplay seconds)
 *   - fade / fade_with_text transition modes
 *   - Touch swipe + keyboard (←→) navigation
 *   - Dots, numbered dots, arrow controls
 *   - Shopify Theme Editor integration (section:load, block:select)
 *   - reveal-on-scroll via IntersectionObserver
 *   - Respects prefers-reduced-motion
 *   - AbortController for safe re-init without listener accumulation
 *   - inert attribute on hidden slides (WCAG 2.1 §2.1.1)
 */

(function () {
  'use strict';

  if (customElements.get('kwolly-slideshow')) return;

  class KwollySlideshow extends HTMLElement {
    /* ── Private fields ─────────────────────────────────── */
    #slides          = [];
    #dots            = [];
    #prevBtn         = null;
    #nextBtn         = null;
    #current         = 0;
    #autoplayMs      = 0;
    #autoplayId      = null;
    #touchStartX     = 0;
    #touchStartY     = 0;
    #reducedMotion   = false;
    #abortController = null;

    /* ── Lifecycle ──────────────────────────────────────── */
    connectedCallback() {
      this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#init();
      this.#listenEditorEvents();
      this.#maybeRevealOnScroll();
    }

    disconnectedCallback() {
      this.#stopAutoplay();
      this.#abortController?.abort();
    }

    /* ── Initialise ─────────────────────────────────────── */
    #init() {
      /* Cancel any previously registered listeners cleanly */
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      /* Re-query DOM (may have changed on section:load) */
      this.#slides  = [...this.querySelectorAll('.kwolly-hero__slide')];
      this.#dots    = [...this.querySelectorAll('[data-kwolly-dots] .kwolly-hero__dot')];
      this.#prevBtn = this.querySelector('[data-kwolly-prev]');
      this.#nextBtn = this.querySelector('[data-kwolly-next]');

      if (this.#slides.length === 0) return;

      /* Locate the initially-selected slide */
      const selected = this.#slides.findIndex(s => s.classList.contains('is-selected'));
      this.#current = Math.max(selected, 0);

      /* Apply inert to all hidden slides (WCAG §2.1.1) and sync ARIA */
      this.#syncState();

      /* Wire up controls */
      this.#prevBtn?.addEventListener('click', () => this.#navigate(-1), { signal });
      this.#nextBtn?.addEventListener('click', () => this.#navigate(1),  { signal });

      this.#dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.#goTo(i), { signal });
      });

      /* Keyboard navigation scoped to this element */
      this.addEventListener('keydown', (e) => this.#handleKeydown(e), { signal });

      /* Touch/swipe */
      this.addEventListener('touchstart', (e) => this.#onTouchStart(e), { signal, passive: true });
      this.addEventListener('touchend',   (e) => this.#onTouchEnd(e),   { signal, passive: true });

      /* Autoplay */
      this.#stopAutoplay();
      const seconds = parseInt(this.dataset.autoplay, 10);
      if (!isNaN(seconds) && seconds > 0) {
        this.#autoplayMs = seconds * 1000;
        this.#startAutoplay();

        /* Pause autoplay while user hovers (desktop) */
        this.addEventListener('mouseenter', () => this.#stopAutoplay(),  { signal });
        this.addEventListener('mouseleave', () => this.#startAutoplay(), { signal });

        /* Pause while focused */
        this.addEventListener('focusin',  () => this.#stopAutoplay(),  { signal });
        this.addEventListener('focusout', () => this.#startAutoplay(), { signal });
      }
    }

    /* ── Navigate by offset (-1 / +1) ──────────────────── */
    #navigate(dir) {
      const next = (this.#current + dir + this.#slides.length) % this.#slides.length;
      this.#goTo(next);
    }

    /* ── Go to a specific slide index ──────────────────── */
    #goTo(index) {
      if (index === this.#current) return;
      if (index < 0 || index >= this.#slides.length) return;

      const leaving  = this.#slides[this.#current];
      const entering = this.#slides[index];

      leaving.classList.remove('is-selected');
      entering.classList.add('is-selected');

      this.#current = index;
      this.#syncState();
      this.#resetAutoplay();
    }

    /* ── Sync dots, ARIA, and inert on all slides ───────── */
    #syncState() {
      this.#dots.forEach((dot, i) => {
        const active = i === this.#current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.#slides.forEach((slide, i) => {
        const active = i === this.#current;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        /* inert hides the slide from AT and removes all focusable children */
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });
    }

    /* ── Autoplay ───────────────────────────────────────── */
    #startAutoplay() {
      if (this.#autoplayMs <= 0 || this.#slides.length < 2) return;
      this.#stopAutoplay();
      this.#autoplayId = setInterval(() => this.#navigate(1), this.#autoplayMs);
    }

    #stopAutoplay() {
      if (this.#autoplayId) {
        clearInterval(this.#autoplayId);
        this.#autoplayId = null;
      }
    }

    #resetAutoplay() {
      if (this.#autoplayMs > 0) this.#startAutoplay();
    }

    /* ── Keyboard ───────────────────────────────────────── */
    #handleKeydown(e) {
      if (this.#slides.length < 2) return;

      /* Only intercept arrow keys when focus is inside the slideshow,
         but NOT inside a text input or textarea */
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.#navigate(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.#navigate(1);
          break;
      }
    }

    /* ── Swipe ──────────────────────────────────────────── */
    #onTouchStart(e) {
      this.#touchStartX = e.touches[0].clientX;
      this.#touchStartY = e.touches[0].clientY;
    }

    #onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.#touchStartX;
      const dy = e.changedTouches[0].clientY - this.#touchStartY;

      /* Only respond to predominantly horizontal swipes > 44px */
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 44) {
        this.#navigate(dx < 0 ? 1 : -1);
      }
    }

    /* ── Shopify Theme Editor events ────────────────────── */
    #listenEditorEvents() {
      document.addEventListener('shopify:section:load', (e) => {
        if (this.closest(`[id="shopify-section-${e.detail.sectionId}"]`)) {
          this.#stopAutoplay();
          this.#init();
        }
      });

      document.addEventListener('shopify:block:select', (e) => {
        /* Find the slide whose data-shopify-block-id matches */
        const target = this.querySelector(`[data-shopify-block-id="${e.detail.blockId}"]`);
        if (!target) return;

        const idx = parseInt(target.dataset.slideIndex ?? '-1', 10);
        if (idx >= 0) {
          this.#stopAutoplay();
          this.#goTo(idx);
        }
      });

      document.addEventListener('shopify:block:deselect', () => {
        if (this.#autoplayMs > 0) this.#startAutoplay();
      });
    }

    /* ── Reveal on scroll (show_initial_transition) ─────── */
    #maybeRevealOnScroll() {
      if (!this.hasAttribute('data-reveal-on-scroll')) return;

      if (this.#reducedMotion) {
        this.classList.add('is-revealed');
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.classList.add('is-revealed');
            io.disconnect();
          }
        },
        { threshold: 0.08 }
      );
      io.observe(this);
    }
  }

  customElements.define('kwolly-slideshow', KwollySlideshow);
})();
